// React
import React, { Fragment, useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { connect as reduxConnect } from 'react-redux'
import { compose } from 'redux'

// Components
import {
  Controls,
  ActionButton,
  QuantityButton,
  QuantityInput,
  Price,
  Image,
  Info,
  Title,
  Variations,
  Contents,
  Availability
} from './styles'
import FocalImage from 'ui/components/FocalImage'
import Skeleton from 'ui/blocks/Skeleton'
import BundleContents from './BundleContents'

// Libraries
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMinus as farMinus, faPlus as farPlus } from '@fortawesome/pro-regular-svg-icons'
import { faTrash as falTrash } from '@fortawesome/pro-light-svg-icons'

import _debounce from 'lodash/debounce'

// Shared
import { Settings as SettingsUtils, Format as FormatUtils } from 'back_office/v2/utils/utils'
import CartActions from 'store/v2/redux/actions/cart'
import NO_PRODUCT_IMAGE from '@assets/images/no-product-image.png'
import { withTranslation } from 'shared/utils/withTranslation'

const Line = ({
  line,
  product,
  currency,
  type,
  hasDates,
  useAvailability,
  hideAvailabilityQuantity,
  showPrices,
  updateLine,
  t
}) => {
  const [quantity, setQuantity] = useState(line?.quantity)
  const [loading, setLoading] = useState(false)

  const handleUpdateQuantity = useCallback(
    (quantity, skipQuantityUpdate) => {
      if (quantity === '') {
        if (!skipQuantityUpdate) {
          setQuantity(1)
        }

        updateLine(line, 1).finally(() => setLoading(false))
      } else {
        if (!skipQuantityUpdate) {
          setQuantity(quantity)
        }

        updateLine(line, quantity).finally(() => setLoading(false))
      }
    },
    [line]
  )

  const debounceUpdateQuantity = _debounce(handleUpdateQuantity, 500)

  const handleUpdateInputQuantity = useCallback(({ target }) => {
    setQuantity(target.value)

    debounceUpdateQuantity(target.value)
  }, [])

  const handleIncreaseQuantity = useCallback((quantity) => {
    setLoading(true)
    setQuantity(quantity + 1)

    debounceUpdateQuantity(quantity + 1, true)
  }, [])

  const handleDecreaseQuantity = useCallback((quantity) => {
    setLoading(true)
    setQuantity(quantity - 1)

    debounceUpdateQuantity(quantity - 1, true)
  }, [])

  const handleDeleteLine = useCallback(() => {
    updateLine(line, 0)
  }, [line])

  if (!line || !product) return null

  const available = line.available_quantity >= quantity

  return (
    <Fragment>
      <Image>
        <FocalImage
          coordinates={line.photo?.coordinates ?? { x: 0, y: 0 }}
          url={line.photo?.large_url ?? NO_PRODUCT_IMAGE}
          size="60px"
        />
      </Image>
      <Info>
        <Title>{product.group_name || product.name}</Title>
        {product.variation_values && <Variations>{product.variation_values.filter((v) => v).join(',')}</Variations>}
        {hasDates && useAvailability && (
          <Availability available={available}>
            {!hideAvailabilityQuantity && (
              <Fragment>
                {line.available_quantity} {t('user.store.available')?.toLowerCase?.()}
              </Fragment>
            )}
            {hideAvailabilityQuantity && t(available ? 'user.store.available' : 'user.store.unavailable')}
          </Availability>
        )}
        {type === 'bundle' && (
          <Contents>
            <BundleContents data={line.items} />
          </Contents>
        )}
      </Info>
      <Controls withRightOffset={showPrices}>
        <ActionButton data-tid={`${product.group_name || product.name}: delete line`} onClick={handleDeleteLine}>
          <FontAwesomeIcon icon={falTrash} />
        </ActionButton>
        <QuantityButton
          data-tid={`${product.group_name || product.name}: decrease quantity`}
          onClick={() => handleDecreaseQuantity(quantity)}
        >
          <FontAwesomeIcon icon={farMinus} />
        </QuantityButton>
        <QuantityInput
          data-tid={`${product.group_name || product.name}: quantity input`}
          value={quantity}
          onChange={handleUpdateInputQuantity}
        />
        <QuantityButton
          data-tid={`${product.group_name || product.name}: increase quantity`}
          onClick={() => handleIncreaseQuantity(quantity)}
        >
          <FontAwesomeIcon icon={farPlus} />
        </QuantityButton>
      </Controls>
      {showPrices && (
        <Price>{loading ? <Skeleton width={60} /> : FormatUtils.money(line.display_price_in_cents, currency)}</Price>
      )}
    </Fragment>
  )
}

Line.displayName = 'Line'

Line.propTypes = {
  line: PropTypes.object,
  product: PropTypes.object,
  currency: PropTypes.object,
  type: PropTypes.oneOf(['rental', 'bundle']),
  hasDates: PropTypes.bool,
  useAvailability: PropTypes.bool,
  hideAvailabilityQuantity: PropTypes.bool,
  showPrices: PropTypes.bool,
  updateLine: PropTypes.func,
  t: PropTypes.func
}

const mapStateToProps = (state, props) => {
  let product

  const cartId = state.client.cartId
  const currency = state.client.currency.toJS()

  const cart = state.orm.carts.getById(cartId)
  const line = state.orm.lines.getById(props.id)
  const type = line?.product?.product_type

  const hasDates = !!cart.starts_at && !!cart.stops_at
  const useAvailability = SettingsUtils.get('store.use_availability') && line?.tracking_type !== 'none'
  const hideAvailabilityQuantity = SettingsUtils.get('store.hide_product_availability_quantities')
  const showPrices = SettingsUtils.get('store.use_prices')

  switch (type) {
    case 'bundle':
      product = state.orm.bundles.getById(line?.product.id)
      break
    case undefined:
      product = null
      break
    default:
      product = state.orm.products.getById(line?.product.id)
      break
  }

  return { line, product, currency, type, useAvailability, hasDates, hideAvailabilityQuantity, showPrices }
}

const mapDispatchToProps = () => {
  return {
    updateLine: (line, quantity) =>
      CartActions.book({
        item_id: line.item_id,
        line_id: line.id,
        quantity,
        type: 'set'
      })
  }
}

export default compose(withTranslation('user'), reduxConnect(mapStateToProps, mapDispatchToProps))(Line)
